import {Trans} from '@lingui/macro';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  createFormFieldHoverStyle,
  createFormInputFieldHeight,
} from '../../../../components/accounts/createAccount/createAccountStyles';
import {CreateFormInputDisplay} from '../../../../components/common/createFormInputs/createFormInputDisplay';
import {addCreateFormInputDisabledStyles} from '../../../../components/common/createFormInputs/createFormInputHelpers';
import {SearchDropdown} from '../../../../components/common/dropdowns/search/searchDropdown';
import {OverflowWithCount} from '../../../../components/common/overflow/overflowWithCount';
import {ApiTokenScopeModel} from '../../../../helpers/apiTokenHelpers';
import {useMeasureElement} from '../../../../helpers/hooks/domHookHelpers';

/*
 * Constants.
 */

const chevronWidth = 32; // px.
const dropdownItemHeight = 30; // px.

/*
 * Props.
 */

interface DevelopersApiTokensCreateScopesInputProps {
  scopes: ReadonlyArray<ApiTokenScopeModel>;
  selectedScopes: ReadonlyArray<string>;
  onScopeSelect: HandlerOf<string>;
  isDisabled?: boolean;
}

/*
 * Style.
 */

interface StyledButtonDivProps {
  $isDisabled?: boolean;
}

const StyledButtonDiv = styled.div<StyledButtonDivProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: ${(p) => p.theme.greys.shade20};
  border-radius: 8px;
  padding: 5px 8px;
  height: ${createFormInputFieldHeight}px;

  ${createFormFieldHoverStyle}
  ${(p) => addCreateFormInputDisabledStyles(p.$isDisabled)}
`;

const StyledPlaceholderTextSpan = styled.span`
  color: ${(p) => p.theme.greys.shade60};
  white-space: nowrap;
`;

const StyledOverflowDiv = styled.div`
  font-weight: ${fontWeights.bold};
`;

const StyledSeparatorSpan = styled.span`
  margin-right: 6px;
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const DevelopersApiTokensCreateScopesInput: FC<DevelopersApiTokensCreateScopesInputProps> = (
  props,
) => {
  const {isDisabled, scopes, selectedScopes, onScopeSelect} = props;
  const theme = useUserTheme();
  const [containerRef, {width}] = useMeasureElement();
  const [filteredScopes, setFilteredScopes] = useState(scopes);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!query) {
      setFilteredScopes(scopes);
      return;
    }
    setFilteredScopes(scopes.filter((s) => s.searchValue.includes(query.toLowerCase())));
  }, [query, scopes]);

  return (
    <div ref={containerRef}>
      <CreateFormInputDisplay
        isRequired
        title={<Trans id="developers-api-tokens-create-dialog-select-scopes-title">Scope</Trans>}
        input={
          <PopoverCoordinator
            isDisabled={isDisabled}
            renderButton={() => (
              <StyledButtonDiv $isDisabled={isDisabled}>
                {renderScopesOrPlaceholder(scopes, selectedScopes, width - chevronWidth)}
                <Icon name="chevronDownSmall" color={theme.greys.shade60} />
              </StyledButtonDiv>
            )}
            renderModal={(popoverProps) => (
              <SearchDropdown<ApiTokenScopeModel>
                anchor={popoverProps.anchor}
                onRequestClose={popoverProps.onRequestClose}
                data={filteredScopes}
                title={<Trans id="developers-api-tokens-create-dialog-select-scopes">Select scopes</Trans>}
                query={query}
                onQueryChange={setQuery}
                findRowHeight={() => dropdownItemHeight}
                renderRow={(item: ApiTokenScopeModel) => {
                  const isChecked = selectedScopes.includes(item.value);
                  return (
                    <DropdownItem
                      onSelect={(event) => {
                        event.preventDefault();
                        onScopeSelect(item.value);
                      }}
                      isChecked={isChecked}
                    >
                      <DropdownItemContent>{item.display}</DropdownItemContent>
                      <DropdownItemCheckbox isChecked={isChecked} />
                    </DropdownItem>
                  );
                }}
              />
            )}
          />
        }
      />
    </div>
  );
};

/*
 * Helpers.
 */

function renderScopesOrPlaceholder(
  scopes: ReadonlyArray<ApiTokenScopeModel>,
  selectedScopes: ReadonlyArray<string>,
  overflowMaxWidth: number,
) {
  if (selectedScopes.length === 0) {
    return (
      <StyledPlaceholderTextSpan>
        <Trans id="developers-api-tokens-create-dialog-select-scopes-placeholder">Select scope</Trans>
      </StyledPlaceholderTextSpan>
    );
  }

  const renderElement = (value: string, index: number) => (
    <div>
      {scopes.find((s) => s.value === value)?.display || value}
      {index < selectedScopes.length - 1 && <StyledSeparatorSpan>,</StyledSeparatorSpan>}
    </div>
  );
  const renderOverflowIndicator = (numberOfHiddenScopes: number) => (
    <StyledOverflowDiv>
      <Trans id="developers-api-tokens-create-dialog-hidden-scopes-indicator">+{numberOfHiddenScopes}</Trans>
    </StyledOverflowDiv>
  );

  return (
    <OverflowWithCount<string>
      elements={selectedScopes}
      renderElement={renderElement}
      renderOverflowIndicator={renderOverflowIndicator}
      overflowContainerWidth={overflowMaxWidth}
    />
  );
}
